import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import client from '../api/client';
import { setUnifiedConfiguration } from '../auth/unified';
import PasswordStepUpDialog from '../components/PasswordStepUpDialog';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sso, setSso] = useState({ enabled: false });

  const loadUser = useCallback(async () => {
    setLoading(true);
    let config;
    try {
      config = (await client.get('/auth/sso/config/')).data;
      setUnifiedConfiguration(config);
      setSso(config);
    } catch {
      setUser(null);
      setSso({ enabled: false, configurationError: true });
      setLoading(false);
      return;
    }
    if (config.enabled) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
    const token = localStorage.getItem('access_token');
    if (!config.enabled && !token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await client.get('/auth/me/', { skipUnifiedRedirect: true });
      setUser(data);
    } catch {
      setUser(null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // 开发态 StrictMode 会把本 effect 跑两遍；相同 GET 由 api/client 合并。
    loadUser();
  }, [loadUser]);

  const login = (userData, accessToken, refreshToken) => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    setUser(userData);
  };

  const logout = async () => {
    if (sso.enabled) {
      const { data } = await client.post('/auth/sso/logout/');
      // Do not clear `user` first: PrivateRoute would start a new SSO login
      // before Keycloak logout navigation runs, so /profile looks stuck.
      if (typeof data?.logout_url !== 'string' || !data.logout_url) {
        throw new Error('logout url missing');
      }
      window.location.assign(data.logout_url);
      return;
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, loadUser, sso }}>
      {children}
      <PasswordStepUpDialog accountUrl={sso.account_url} callbackOrigin={sso.password_callback_origin} />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
